import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { TSupportedJurisdiction, TSupportedLanguage } from '../types/patentDraft';

interface IPatentDraftState {
  name: string | null;
  jurisdiction: TSupportedJurisdiction;
  inventionDescription: string;
  highlightMode: 'before' | 'after' | 'highlight' | null;
  findAndReplaceMode: boolean;
  language: TSupportedLanguage;
  filesUploading: number;
  isGenerating: boolean;
}

const initialState: IPatentDraftState = {
  name: '',
  jurisdiction: TSupportedJurisdiction.EPO,
  inventionDescription: '',
  highlightMode: null,
  findAndReplaceMode: false,
  language: TSupportedLanguage.ENGLISH,
  filesUploading: 0,
  isGenerating: false,
};

const patentDraftSectionsSlice = createSlice({
  name: 'patentDraftSections',
  initialState,
  reducers: {
    setPatentDraft: (state, action: PayloadAction<Omit<IPatentDraftState, 'isGenerating'>>) => {
      state.name = action.payload.name;
      state.jurisdiction = action.payload.jurisdiction;
      state.inventionDescription = action.payload.inventionDescription;
      state.highlightMode = action.payload.highlightMode;
      state.language = action.payload.language;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setJurisdiction: (state, action: PayloadAction<TSupportedJurisdiction>) => {
      state.jurisdiction = action.payload;
    },
    setInventionDescription: (state, action: PayloadAction<string>) => {
      state.inventionDescription = action.payload;
    },
    setHighlightMode: (state, action: PayloadAction<'before' | 'after' | 'highlight' | null>) => {
      state.highlightMode = action.payload;
    },
    setFindAndReplaceMode: (state, action: PayloadAction<boolean>) => {
      state.findAndReplaceMode = action.payload;
    },
    setLanguage: (state, action: PayloadAction<TSupportedLanguage>) => {
      state.language = action.payload;
    },
    setFilesUploading: (state, action: PayloadAction<'add' | 'remove'>) => {
      if (action.payload === 'add') {
        state.filesUploading++;
      } else {
        state.filesUploading--;
      }
    },
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
    },
    clearPatentDraft: () => initialState,
  },
});

export default patentDraftSectionsSlice.reducer;

export const {
  setName,
  setInventionDescription,
  setJurisdiction,
  setPatentDraft,
  clearPatentDraft,
  setFindAndReplaceMode,
  setLanguage,
  setFilesUploading,
  setIsGenerating,
} = patentDraftSectionsSlice.actions;
